import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "components/app/dialog/dialog";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { ScrollArea, ScrollBar } from "components/ui/scroll-area";
import { t } from "i18next";
import { Plus, X } from "lucide-react";

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { AppLog } from "services";
import { useAppSelector } from "store/store";
import { RequestApi, RouteApi, StatusMsg } from "utils";

interface DialogProps {
  categories: any[];
}

interface ItemGroup {
  id: number;
  shop_group: string;
  code: string;
}

export default function DailogCreateYourPage({ categories }: DialogProps) {
  const [itemGroups, setItemGroups] = useState<any[]>(categories);
  const [textInput, setTextInput] = useState<string>();
  const [pageName, setPageName] = useState<string>();
  const { token } = useAppSelector((state) => state.auth);
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const navigate = useNavigate();

  const submitProfileApi = async () => {
    try {
      const url = `${RouteApi.submitInsertUserPage}token=${token}`;
      const data = {
        page_name: pageName,
        item_group: textInput,
      };
      const response = await RequestApi(url, { data });
      if (response.status === "success") {
        navigate(`/profile?id=${response.page_user.id}&posts`);
      }
    } catch (error) {
      AppLog.show({
        message: `[submitProfileApi]`,
        status: StatusMsg.error,
        error,
      });
    }
  };

  const handleSave = () => {
    if (!textInput || !pageName) {
      if (!pageName) {
        toast.error("Please fill your page name");
      } else {
        toast.error("Please choose category");
      }
    } else {
      submitProfileApi();
      setIsDialogOpen(false);
    }
  };

  const groupedByCategory: Record<string, ItemGroup[]> = itemGroups?.reduce((acc: Record<string, ItemGroup[]>, group: ItemGroup) => {
    const category = group.shop_group || "Uncategorized";
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(group);
    return acc;
  }, {});

  useEffect(() => {
    if (isDialogOpen) {
      setItemGroups(categories);
    }
  }, [isDialogOpen, categories]);

  return (
    <Dialog open={isDialogOpen}>
      <DialogTrigger onClick={() => setIsDialogOpen(true)} className="" asChild>
        <div className="flex items-center justify-start flex-row cursor-pointer hover:bg-slate-200 dark:bg-background dark:hover:bg-slate-900 w-full p-2 rounded-lg">
          <div className="w-8 h-8 rounded-full border flex justify-center items-center bg-gray-200 dark:bg-background dark:hover:bg-slate-900">
            <Plus />
          </div>
          <button className="ml-2 font-medium">{t("Create your page")}</button>
        </div>
      </DialogTrigger>
      <DialogContent isShowBtnXClose={false} className="custom-dialog-class flex flex-col justify-between">
        <DialogHeader>
          <DialogTitle>{t("Create New Page")}</DialogTitle>
          <DialogDescription>Make changes to your profile here. Click save when you're done.</DialogDescription>
          <div
            className="absolute flex items-center justify-center right-4 top-1 z-40 cursor-pointer rounded-full dark:bg-background dark:hover:bg-slate-900 w-[28px] h-[28px]"
            onClick={() => setIsDialogOpen(false)}
          >
            <X />
          </div>
        </DialogHeader>
        <div className="space-y-1">
          <Label htmlFor="name" className="text-right">
            Name
          </Label>
          <Input id="name" onChange={(event) => setPageName(event.target.value)} className="" />
        </div>
        <ScrollArea onWheel={(e) => e.stopPropagation()} className="mt-2 w-full h-[400px]">
          {Object.entries(groupedByCategory || {}).map(([category, groups]) => (
            <div key={category} className="mb-2">
              <h3 className="font-bold">{category}</h3>
              <div className="flex flex-wrap">
                {groups.map((group) => {
                  const isSelected = textInput === group?.code.toString();
                  return (
                    <div
                      key={group.id || group.code} // Ensure a unique key
                      className={`p-1 border rounded-xl px-2 mr-2 cursor-pointer font-semibold mt-2 ${isSelected ? "bg-blue-500 text-white" : " dark:text-white border-1"}`}
                      onClick={() => {
                        setTextInput(group?.code.toString());
                      }}
                    >
                      <p className="text-nowrap text-sm">{group?.code}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          <ScrollBar orientation="vertical" />
        </ScrollArea>
        <DialogFooter>
          <Button className="bg-destructive text-white bg-blue-600 hover:bg-blue-500" onClick={handleSave}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
